import { api } from './client'
import type { CursorPage, Deal, DealContactLink, DealContactRole } from './types'

export async function boardDeals(pipelineId: string): Promise<Deal[]> {
  const { data } = await api.get<Deal[]>('/api/deals/board', { params: { pipelineId } })
  return data
}

/** A flat page of the agency's deals across pipelines — backs the contact card's "Link to deal" picker. */
export async function listDeals(limit = 100): Promise<Deal[]> {
  const { data } = await api.get<CursorPage<Deal>>('/api/deals', { params: { limit } })
  return data.items
}

export interface CreateDealParams {
  title: string
  pipelineId: string
  stageId: string
  value?: number | null
  primaryContactId?: string | null
  nextContactDate?: string | null
  custom?: Record<string, unknown>
}

export async function createDeal(params: CreateDealParams): Promise<Deal> {
  const { data } = await api.post<Deal>('/api/deals', params)
  return data
}

export async function getDeal(id: string): Promise<Deal> {
  const { data } = await api.get<Deal>(`/api/deals/${id}`)
  return data
}

export interface UpdateDealParams {
  title: string
  ownerUserId?: string | null
  primaryContactId?: string | null
  value?: number | null
  status?: string | null
  nextContactDate?: string | null
  nextAction?: string | null
  custom?: Record<string, unknown>
}

export async function updateDeal(id: string, params: UpdateDealParams): Promise<Deal> {
  const { data } = await api.put<Deal>(`/api/deals/${id}`, params)
  return data
}

/** Values for fields the target stage requires, saved together with the move. */
export interface MoveFills {
  /** Plans the next step as a task due that day (the deal's next contact date follows its tasks). */
  nextContactDate?: string | null
  /** The next step's title ("Follow up" when absent). */
  nextStepTitle?: string | null
  value?: number | null
  custom?: Record<string, unknown>
}

export async function moveDeal(id: string, stageId: string, fills?: MoveFills): Promise<Deal> {
  const { data } = await api.post<Deal>(`/api/deals/${id}/move`, { stageId, ...fills })
  return data
}

/** Save the signed-in user's order of a board column, top first ("my order"). */
export async function saveBoardOrder(dealIds: string[]): Promise<void> {
  await api.put('/api/deals/board-order', { dealIds })
}

export async function deleteDeal(id: string): Promise<void> {
  await api.delete(`/api/deals/${id}`)
}

/** Default role list — fallback when the agency's editable list isn't available. */
export const DEAL_CONTACT_ROLES: DealContactRole[] = [
  'owner',
  'vendor',
  'buyer',
  'solicitor',
  'tenant',
  'contact',
]

export async function listDealContacts(dealId: string): Promise<DealContactLink[]> {
  const { data } = await api.get<DealContactLink[]>(`/api/deals/${dealId}/contacts`)
  return data
}

export interface AddDealContactParams {
  contactId: string
  role: string
}

export async function addDealContact(dealId: string, params: AddDealContactParams): Promise<DealContactLink> {
  const { data } = await api.post<DealContactLink>(`/api/deals/${dealId}/contacts`, params)
  return data
}

export interface ChangeDealContactRoleParams {
  contactId: string
  /** Which link to change — the same contact may hold several roles on one deal. */
  currentRole: string
  role: string
}

/** Re-labels an existing link, so fixing a role doesn't mean unlinking and re-adding the contact. */
export async function changeDealContactRole(
  dealId: string,
  { contactId, currentRole, role }: ChangeDealContactRoleParams,
): Promise<DealContactLink> {
  const { data } = await api.put<DealContactLink>(
    `/api/deals/${dealId}/contacts/${contactId}/role`,
    { currentRole, role },
  )
  return data
}

/** Removes the contact from the deal entirely (all roles). */
export async function removeDealContact(dealId: string, contactId: string): Promise<void> {
  await api.delete(`/api/deals/${dealId}/contacts/${contactId}`)
}

/** Marks an already-linked contact as the deal's primary (the send / AI-draft recipient). */
export async function setDealPrimaryContact(dealId: string, contactId: string): Promise<Deal> {
  const { data } = await api.put<Deal>(`/api/deals/${dealId}/contacts/${contactId}/primary`)
  return data
}

/** Deals whose title (the property address) contains q — the app-wide search box. */
export async function searchDeals(q: string, limit = 6): Promise<Deal[]> {
  const { data } = await api.get<Deal[]>('/api/deals/search', { params: { q, limit } })
  return data
}
